// Compliance card for ItemReportModal's Stats tab (MEDICATION_COMPLIANCE_PLAN.md
// D-MC-1..3, Task 2.1). Presentation only — every number arrives pre-computed
// by buildComplianceStats (shared/src/medicationCompliance.ts).

import { useState } from "react";
import type { ComplianceStats } from "@medassist/shared";
import { TIME_OF_DAY_BUCKETS } from "@medassist/shared";

const DOW_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIME_OF_DAY_LABEL: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};
const TIME_OF_DAY_ORDER = Object.keys(TIME_OF_DAY_BUCKETS) as (keyof typeof TIME_OF_DAY_BUCKETS)[];

function patternColorClass(pct: number): string {
  if (pct >= 80) return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
  if (pct >= 50) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
  return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
}

interface Props {
  stats: ComplianceStats | null;
  medicationName: string;
  windowDays: number;
  onNoSchedule?: () => void;
}

function TrendBadge({ direction, change }: { direction: ComplianceStats["trendDirection"]; change?: number }) {
  if (direction === "insufficient_data" || change === undefined) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">vs previous period: insufficient data</span>;
  }
  const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";
  const sign = change > 0 ? "+" : "";
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      {arrow} {sign}
      {change}%
    </span>
  );
}

function CountBadge({ label, value }: { label: string; value: number }) {
  return (
    <span className="inline-flex items-baseline gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800">
      <span className="font-semibold text-gray-900 dark:text-gray-100">{value}</span>
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
    </span>
  );
}

export function ComplianceCard({ stats, medicationName, windowDays, onNoSchedule }: Props) {
  const [patternsExpanded, setPatternsExpanded] = useState(false);

  if (!stats) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Compliance
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Compliance statistics require a medication schedule. Set one in Regimens to track this.
        </p>
        {onNoSchedule && (
          <button
            type="button"
            onClick={onNoSchedule}
            className="mt-2 min-h-[44px] rounded-md border border-gray-300 px-3 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:min-h-0 sm:py-1.5"
          >
            Set up schedule
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 sm:col-span-2"
      aria-label={`Compliance for ${medicationName}`}
    >
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Compliance
      </h3>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.onTimePercentage}%</div>
        <TrendBadge direction={stats.trendDirection} change={stats.percentageChange} />
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        On-time over the last {windowDays} days
      </p>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <CountBadge label="on-time" value={stats.onTimeCount} />
        <CountBadge label="late" value={stats.lateCount} />
        <CountBadge label="missed" value={stats.missedCount} />
        {stats.earlyCount > 0 && <CountBadge label="early" value={stats.earlyCount} />}
      </div>

      <div className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-300">
        <p>
          Longest streak: {stats.longestOnTimeStreak} day{stats.longestOnTimeStreak === 1 ? "" : "s"}
          {stats.singleOnTimeDays > 0 &&
            ` · Single on-time days: ${stats.singleOnTimeDays}`}
        </p>
        {stats.averageLatenessHours !== undefined && (
          <p>When late, averages {stats.averageLatenessHours}h behind schedule.</p>
        )}
        {stats.earlyCount > 0 &&
          (() => {
            const windowTotal = stats.onTimeCount + stats.lateCount + stats.missedCount + stats.earlyCount;
            const earlyPct = windowTotal > 0 ? Math.round((stats.earlyCount / windowTotal) * 100) : 0;
            return (
              <p>
                Early doses: {stats.earlyCount} ({earlyPct}% of window)
              </p>
            );
          })()}
      </div>

      {(Object.keys(stats.timeOfDayPattern).length > 0 || Object.keys(stats.dayOfWeekPattern).length > 0) && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setPatternsExpanded((v) => !v)}
            className="flex min-h-[44px] w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 sm:min-h-0"
          >
            Patterns
            <span aria-hidden="true">{patternsExpanded ? "−" : "+"}</span>
          </button>

          {patternsExpanded && (
            <div className="mt-1 space-y-3">
              {Object.keys(stats.timeOfDayPattern).length > 0 && (
                <div>
                  <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Time of day
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {TIME_OF_DAY_ORDER.filter((k) => stats.timeOfDayPattern[k] !== undefined).map((k) => (
                      <span
                        key={k}
                        className={`inline-flex items-baseline gap-1 rounded-md px-2 py-1 text-xs ${patternColorClass(stats.timeOfDayPattern[k])}`}
                      >
                        <span>{TIME_OF_DAY_LABEL[k]}</span>
                        <span className="font-medium">{stats.timeOfDayPattern[k]}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {Object.keys(stats.dayOfWeekPattern).length > 0 && (
                <div>
                  <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Day of week
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {DOW_LABEL.map((label, i) =>
                      stats.dayOfWeekPattern[i] !== undefined ? (
                        <span
                          key={i}
                          className={`inline-flex items-baseline gap-1 rounded-md px-2 py-1 text-xs ${patternColorClass(stats.dayOfWeekPattern[i])}`}
                        >
                          <span>{label}</span>
                          <span className="font-medium">{stats.dayOfWeekPattern[i]}%</span>
                        </span>
                      ) : null
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
